import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout, { PageHeader } from "../components/AppLayout";
import { getFindingsLibrary } from "../lib/api";
import FindingReviewControls, {
    ReviewStatusPill,
} from "../components/FindingReviewControls";
import {
    AlertTriangle,
    ChevronDown,
    ChevronRight,
    Search,
    Filter,
    ExternalLink,
    Loader2,
    TrendingUp,
    FileSearch,
    FileDown,
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const SEV_ORDER = ["Critical", "Major", "Minor", "Observation"];
const SEV_STYLE = {
    Critical: {
        pill: "bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]",
        bar: "bg-[#DC2626]",
        label: "text-[#991B1B]",
    },
    Major: {
        pill: "bg-[#FFEDD5] text-[#9A3412] border-[#FED7AA]",
        bar: "bg-[#EA580C]",
        label: "text-[#9A3412]",
    },
    Minor: {
        pill: "bg-[#FEF3C7] text-[#854D0E] border-[#FDE68A]",
        bar: "bg-[#CA8A04]",
        label: "text-[#854D0E]",
    },
    Observation: {
        pill: "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]",
        bar: "bg-[#16A34A]",
        label: "text-[#166534]",
    },
};

const Stat = ({ label, value, sub, accent, testid }) => (
    <div
        className="p-8 border-r border-b border-hair bg-white"
        data-testid={testid}
    >
        <div className="overline mb-4">{label}</div>
        <div
            className={`heading text-4xl font-medium tracking-tighter leading-none ${
                accent ? "text-[#C2410C]" : ""
            }`}
        >
            {value}
        </div>
        {sub && <div className="text-xs text-neutral-500 mt-3 mono">{sub}</div>}
    </div>
);

const SeverityPill = ({ sev }) => {
    const s = SEV_STYLE[sev] || SEV_STYLE.Minor;
    return (
        <span
            className={`mono text-[10px] uppercase tracking-wider px-2 py-[2px] border rounded-sm ${s.pill}`}
        >
            {sev}
        </span>
    );
};

const StatusPill = ({ status }) => {
    const isOpen = status === "Open";
    return (
        <span
            className={`mono text-[10px] uppercase tracking-wider px-2 py-[2px] border rounded-sm ${
                isOpen
                    ? "border-[#FCA5A5] bg-[#FEF2F2] text-[#991B1B]"
                    : "border-hair bg-neutral-50 text-neutral-500"
            }`}
        >
            {status}
        </span>
    );
};

// Horizontal stacked bar (Critical / Major / Minor / Observation)
const SeverityBar = ({ counts, total }) => {
    if (!total) return null;
    return (
        <div className="flex h-[8px] rounded-sm overflow-hidden border border-hair">
            {SEV_ORDER.map((sev) => {
                const n = counts[sev] || 0;
                if (!n) return null;
                const pct = Math.max(2, Math.round((n / total) * 100));
                return (
                    <div
                        key={sev}
                        className={SEV_STYLE[sev].bar}
                        style={{ width: `${pct}%` }}
                        title={`${sev} · ${n}`}
                    />
                );
            })}
        </div>
    );
};

export default function FindingsLibrary() {
    const [data, setData] = useState(null);
    const [severity, setSeverity] = useState("All");
    const [status, setStatus] = useState("All");
    const [query, setQuery] = useState("");
    const [expanded, setExpanded] = useState(null);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    const handleExport = async () => {
        if (exporting) return;
        setExporting(true);
        const t = toast.loading("Generating inspection-ready PDF…");
        try {
            const params = {};
            if (severity !== "All") params.severity = severity;
            if (status !== "All") params.status = status;
            const res = await api.get("/findings.pdf", {
                params,
                responseType: "blob",
            });
            const url = URL.createObjectURL(res.data);
            const a = window.document.createElement("a");
            a.href = url;
            const sevTag = severity === "All" ? "all" : severity.toLowerCase();
            const stTag = status === "All" ? "all" : status.toLowerCase();
            a.download = `audri_findings_${sevTag}_${stTag}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Report exported.", { id: t });
        } catch (e) {
            toast.error(
                e?.response?.status === 403
                    ? "Read-only demo — export is disabled here. Contact sales for a pilot."
                    : "Export failed. Please try again.",
                { id: t },
            );
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getFindingsLibrary({ severity, status })
            .then((d) => {
                if (!cancelled) setData(d);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [severity, status]);

    const filtered = useMemo(() => {
        if (!data?.findings) return [];
        const q = query.trim().toLowerCase();
        if (!q) return data.findings;
        return data.findings.filter(
            (f) =>
                (f.title || "").toLowerCase().includes(q) ||
                (f.description || "").toLowerCase().includes(q) ||
                (f.system_name || "").toLowerCase().includes(q) ||
                (f.sop_citation || "").toLowerCase().includes(q),
        );
    }, [data, query]);

    const totals = data?.totals || {};
    const sevCounts = data?.severity_counts || {};

    return (
        <AppLayout>
            <PageHeader
                eyebrow="Workspace · Compliance"
                title="Findings"
                sub="Every audit finding across your GxP estate — one view, triaged by severity and tied back to the rule that generated it."
                right={
                    <div className="flex gap-3 flex-wrap items-center">
                        <div className="relative">
                            <Search
                                size={13}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
                            />
                            <input
                                data-testid="findings-search"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Filter by title, system, SOP…"
                                className="text-sm border border-hair pl-8 pr-3 py-2 rounded-sm bg-white w-72 focus:outline-none focus:border-[#C2410C]"
                            />
                        </div>
                        <div className="inline-flex items-center border border-hair rounded-sm bg-white text-sm overflow-hidden">
                            <span className="px-3 py-2 overline border-r border-hair flex items-center gap-1 text-neutral-500">
                                <Filter size={12} /> STATUS
                            </span>
                            {["All", "Open", "Closed"].map((s) => (
                                <button
                                    key={s}
                                    data-testid={`findings-status-${s}`}
                                    onClick={() => setStatus(s)}
                                    className={`px-3 py-2 border-r border-hair last:border-r-0 ${
                                        status === s
                                            ? "bg-[#0a0a0a] text-white"
                                            : "hover:bg-neutral-50"
                                    }`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={handleExport}
                            disabled={exporting || !filtered.length}
                            data-testid="btn-findings-export"
                            title={
                                filtered.length
                                    ? "Download this view as an inspection-ready PDF"
                                    : "No findings in current view"
                            }
                            className="inline-flex items-center gap-2 bg-[#C2410C] text-white text-sm px-4 py-2 rounded-sm hover:bg-[#9A3412] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {exporting ? (
                                <Loader2 size={13} className="animate-spin" />
                            ) : (
                                <FileDown size={13} />
                            )}
                            Export this view
                        </button>
                    </div>
                }
            />

            {/* Severity KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-5 border-l border-hair">
                <Stat
                    label="Total findings"
                    value={totals.total ?? "—"}
                    sub={`${totals.open ?? 0} open`}
                    testid="findings-kpi-total"
                />
                <Stat
                    label="Critical"
                    value={sevCounts.Critical ?? 0}
                    sub="Regulatory risk"
                    testid="findings-kpi-critical"
                    accent
                />
                <Stat
                    label="Major"
                    value={sevCounts.Major ?? 0}
                    sub="Significant gaps"
                    testid="findings-kpi-major"
                />
                <Stat
                    label="Minor / Obs."
                    value={(sevCounts.Minor || 0) + (sevCounts.Observation || 0)}
                    sub="Observation class"
                    testid="findings-kpi-minor"
                />
                <Stat
                    label="Systems affected"
                    value={totals.systems_affected ?? 0}
                    sub="GxP systems with findings"
                    testid="findings-kpi-systems"
                />
            </div>

            {/* Severity bar + top offenders */}
            <div className="px-8 md:px-12 pt-8 grid md:grid-cols-3 gap-6">
                <div className="md:col-span-1 border border-hair bg-white p-6">
                    <div className="overline mb-4">Severity mix</div>
                    <SeverityBar counts={sevCounts} total={totals.total || 0} />
                    <div className="grid grid-cols-2 gap-y-2 gap-x-4 mt-5 text-[12px]">
                        {SEV_ORDER.map((sev) => (
                            <div
                                key={sev}
                                className="flex items-center gap-2"
                            >
                                <span
                                    className={`w-[10px] h-[10px] rounded-sm ${SEV_STYLE[sev].bar}`}
                                />
                                <span className={SEV_STYLE[sev].label}>
                                    {sev}
                                </span>
                                <span className="num ml-auto text-neutral-600">
                                    {sevCounts[sev] || 0}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
                <div
                    className="border border-hair bg-white p-6"
                    data-testid="findings-top-systems"
                >
                    <div className="overline mb-4 flex items-center gap-2">
                        <TrendingUp size={11} /> Worst systems
                    </div>
                    {data?.top_systems?.length ? (
                        <ul className="space-y-2 text-sm">
                            {data.top_systems.map((s) => (
                                <li
                                    key={s.name}
                                    className="flex items-center justify-between"
                                >
                                    <span className="heading tracking-tight font-medium truncate">
                                        {s.name}
                                    </span>
                                    <span className="num text-neutral-600 text-[13px]">
                                        {s.count}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="text-sm text-neutral-500">
                            No findings in current filter.
                        </div>
                    )}
                </div>
                <div
                    className="border border-hair bg-white p-6"
                    data-testid="findings-top-sops"
                >
                    <div className="overline mb-4 flex items-center gap-2">
                        <FileSearch size={11} /> SOPs driving findings
                    </div>
                    {data?.top_sops?.length ? (
                        <ul className="space-y-2 text-sm">
                            {data.top_sops.map((s) => (
                                <li
                                    key={s.name}
                                    className="flex items-center justify-between"
                                >
                                    <span className="truncate">{s.name}</span>
                                    <span className="num text-neutral-600 text-[13px]">
                                        {s.count}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="text-sm text-neutral-500">
                            Findings aren't attributed to a SOP yet (run an audit
                            that cites rules by sop_citation).
                        </div>
                    )}
                </div>
            </div>

            {/* Severity filter tabs */}
            <div className="px-8 md:px-12 pt-10 flex gap-2 flex-wrap">
                {["All", ...SEV_ORDER].map((sev) => {
                    const count =
                        sev === "All"
                            ? totals.total ?? 0
                            : sevCounts[sev] || 0;
                    const active = severity === sev;
                    return (
                        <button
                            key={sev}
                            data-testid={`findings-sev-${sev}`}
                            onClick={() => setSeverity(sev)}
                            className={`inline-flex items-center gap-2 border text-xs px-3 py-2 rounded-sm transition-colors ${
                                active
                                    ? "bg-[#0a0a0a] text-white border-[#0a0a0a]"
                                    : "border-hair bg-white hover:bg-neutral-50"
                            }`}
                        >
                            {sev !== "All" && (
                                <span
                                    className={`w-[8px] h-[8px] rounded-sm ${SEV_STYLE[sev].bar}`}
                                />
                            )}
                            {sev}
                            <span
                                className={`mono ${
                                    active
                                        ? "text-white/80"
                                        : "text-neutral-500"
                                }`}
                            >
                                {count}
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* Table */}
            <div className="px-8 md:px-12 pt-6 pb-12">
                {loading && (
                    <div className="py-16 text-center text-neutral-500 text-sm">
                        <Loader2 className="animate-spin inline mr-2" size={14} />
                        Loading findings…
                    </div>
                )}
                {!loading && filtered.length === 0 && (
                    <div
                        data-testid="findings-empty"
                        className="border border-dashed border-hair bg-white p-16 text-center"
                    >
                        <AlertTriangle
                            className="mx-auto text-neutral-300"
                            size={32}
                        />
                        <p className="text-sm text-neutral-500 mt-4">
                            {query || severity !== "All" || status !== "All"
                                ? "No findings match your filter. Try loosening it."
                                : "No findings yet. Run a periodic review on any system to generate findings."}
                        </p>
                    </div>
                )}

                {!loading && filtered.length > 0 && (
                    <div
                        className="border border-hair bg-white"
                        data-testid="findings-table"
                    >
                        <div className="grid grid-cols-[24px_110px_minmax(0,3fr)_minmax(0,1.5fr)_90px_110px_120px] px-4 py-3 border-b border-hair overline text-[10px] gap-4">
                            <div />
                            <div>Severity</div>
                            <div>Title</div>
                            <div>System</div>
                            <div>Status</div>
                            <div>Gap type</div>
                            <div className="text-right">Detected</div>
                        </div>

                        {filtered.map((f) => {
                            const isOpen = expanded === f.id;
                            return (
                                <div
                                    key={f.id}
                                    className="border-b border-hair last:border-b-0"
                                    data-testid={`finding-row-${f.finding_id}`}
                                >
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setExpanded(isOpen ? null : f.id)
                                        }
                                        className={`w-full grid grid-cols-[24px_110px_minmax(0,3fr)_minmax(0,1.5fr)_90px_110px_120px] px-4 py-3 items-center text-left gap-4 hover:bg-neutral-50 ${
                                            isOpen ? "bg-neutral-50" : ""
                                        }`}
                                    >
                                        <span className="text-neutral-400">
                                            {isOpen ? (
                                                <ChevronDown size={14} />
                                            ) : (
                                                <ChevronRight size={14} />
                                            )}
                                        </span>
                                        <span>
                                            <SeverityPill sev={f.severity} />
                                        </span>
                                        <span className="text-sm truncate flex items-center gap-2 min-w-0">
                                            <span className="mono text-[10px] text-neutral-400 shrink-0">
                                                {f.finding_id}
                                            </span>
                                            <span className="truncate">
                                                {f.title}
                                            </span>
                                            <ReviewStatusPill
                                                status={f.review_status}
                                            />
                                        </span>
                                        <span className="heading tracking-tight font-medium text-sm truncate">
                                            {f.system_name}
                                        </span>
                                        <span>
                                            <StatusPill status={f.status} />
                                        </span>
                                        <span className="text-xs text-neutral-600 truncate">
                                            {f.gap_type}
                                        </span>
                                        <span className="mono text-[11px] text-right text-neutral-500">
                                            {f.created_at
                                                ? new Date(
                                                      f.created_at,
                                                  ).toLocaleDateString()
                                                : "—"}
                                        </span>
                                    </button>

                                    {isOpen && (
                                        <div
                                            className="px-8 py-6 border-t border-hair bg-[#fafafa]"
                                            data-testid={`finding-expanded-${f.finding_id}`}
                                        >
                                            <div className="grid md:grid-cols-5 gap-8">
                                                <div className="md:col-span-3">
                                                    <div className="overline mb-2">
                                                        Description
                                                    </div>
                                                    <p className="text-[13.5px] leading-relaxed text-neutral-700 whitespace-pre-wrap">
                                                        {f.description ||
                                                            "(no description)"}
                                                    </p>

                                                    <div className="overline mt-6 mb-2">
                                                        CAPA · AI-drafted
                                                    </div>
                                                    <p className="text-[13.5px] leading-relaxed text-neutral-700 whitespace-pre-wrap border-l-2 border-[#C2410C] pl-4">
                                                        {f.capa ||
                                                            "(no CAPA drafted — rerun audit or edit manually)"}
                                                    </p>
                                                </div>

                                                <div className="md:col-span-2">
                                                    <div className="overline mb-3">
                                                        Traceability
                                                    </div>
                                                    <dl className="text-sm space-y-2">
                                                        <div className="flex justify-between gap-3">
                                                            <dt className="text-neutral-500">
                                                                System
                                                            </dt>
                                                            <dd>
                                                                <Link
                                                                    to={`/systems/${f.system_id}`}
                                                                    data-testid={`finding-syslink-${f.finding_id}`}
                                                                    className="inline-flex items-center gap-1 text-[#C2410C] hover:underline heading tracking-tight font-medium"
                                                                >
                                                                    {f.system_name}
                                                                    <ExternalLink
                                                                        size={
                                                                            11
                                                                        }
                                                                    />
                                                                </Link>
                                                            </dd>
                                                        </div>
                                                        <div className="flex justify-between gap-3">
                                                            <dt className="text-neutral-500">
                                                                Severity
                                                            </dt>
                                                            <dd>
                                                                <SeverityPill
                                                                    sev={
                                                                        f.severity
                                                                    }
                                                                />
                                                            </dd>
                                                        </div>
                                                        <div className="flex justify-between gap-3">
                                                            <dt className="text-neutral-500">
                                                                Status
                                                            </dt>
                                                            <dd>
                                                                <StatusPill
                                                                    status={
                                                                        f.status
                                                                    }
                                                                />
                                                            </dd>
                                                        </div>
                                                        <div className="flex justify-between gap-3">
                                                            <dt className="text-neutral-500">
                                                                Gap type
                                                            </dt>
                                                            <dd className="mono text-[12px]">
                                                                {f.gap_type ||
                                                                    "—"}
                                                            </dd>
                                                        </div>
                                                        <div className="flex justify-between gap-3">
                                                            <dt className="text-neutral-500">
                                                                Rule
                                                            </dt>
                                                            <dd className="mono text-[11px] text-right truncate max-w-[55%]">
                                                                {f.rule_id ||
                                                                    "—"}
                                                            </dd>
                                                        </div>
                                                        {f.sop_citation && (
                                                            <div className="flex justify-between gap-3">
                                                                <dt className="text-neutral-500">
                                                                    SOP citation
                                                                </dt>
                                                                <dd className="text-[12px] text-right truncate max-w-[55%]">
                                                                    {
                                                                        f.sop_citation
                                                                    }
                                                                </dd>
                                                            </div>
                                                        )}
                                                        <div className="flex justify-between gap-3">
                                                            <dt className="text-neutral-500">
                                                                Detected
                                                            </dt>
                                                            <dd className="mono text-[11px]">
                                                                {f.created_at
                                                                    ? new Date(
                                                                          f.created_at,
                                                                      ).toLocaleString()
                                                                    : "—"}
                                                            </dd>
                                                        </div>
                                                    </dl>
                                                </div>
                                            </div>
                                            <FindingReviewControls
                                                finding={f}
                                                onChange={(updated) =>
                                                    setData((prev) =>
                                                        prev
                                                            ? {
                                                                  ...prev,
                                                                  findings:
                                                                      prev.findings.map(
                                                                          (x) =>
                                                                              x.id ===
                                                                              f.id
                                                                                  ? {
                                                                                        ...x,
                                                                                        ...updated,
                                                                                    }
                                                                                  : x,
                                                                      ),
                                                              }
                                                            : prev,
                                                    )
                                                }
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </AppLayout>
    );
}
